/**
 * Student programs: a stored policy, the human-entered record, and the
 * status a function computed from the two.
 *
 * ## Why the policy is data
 *
 * Senior privileges is the motivating case. The written policy sets a grade
 * floor ("all overall grades are B- or better"), an attendance standard ("no
 * more than 1 unexcused absence or 2 tardies per 2 weeks") and a handful of
 * paper gates. Every one of those numbers is a thing an administrator may
 * reasonably change mid-year, and none of them should cost a deploy. So the
 * thresholds, the windows and the prose all live in a document, and the code
 * holds only the vocabulary for reading it.
 *
 * The vocabulary is deliberately tiny — see `docs/senior-privileges.md` §3 for
 * the argument. A program's criteria are a **flat AND-list**: the student
 * qualifies if every criterion passes. There is no OR, no nesting and no
 * expression language, because policies are written as lists of disqualifiers
 * and "none of these apply" is already a conjunction of negations. "1 unexcused
 * absence or 2 tardies costs you privileges" is
 * `unexcused_absences <= 1 AND tardies <= 2`; De Morgan does the work a boolean
 * builder would otherwise have to expose.
 *
 * ### The one deliberate exception: a waiver
 *
 * A `waiver` criterion is the single place the verdict is not a plain AND. An
 * active waiver — a person deciding "eligible regardless, until this date",
 * with a reason — makes the student GO even when other criteria fail or cannot
 * be checked. It is a hold in reverse, and it is stored exactly as a hold is:
 * dated, attributed, append-only.
 *
 * It is still not an OR in the language. Nobody writes "grades OR waiver"; the
 * policy says *a human may say yes*, and the yes is a record with a name and
 * an end date on it, which is the same argument `ProgramOverride` makes for
 * not having disjuncts. The whole verdict is
 *
 * ```
 * GO  =  no active hold  AND  (an active waiver  OR  every other criterion passes)
 * ```
 *
 * **Holds still win.** A hold is a deliberate no and a waiver a deliberate
 * yes; when both are in force somebody has decided against the student since
 * somebody else decided for them, and the safer, more recent-sounding answer
 * is the no. A waiver criterion with no waiver in force is neutral — it is not
 * a rule anybody can fail — so it never appears among the outcomes at all. See
 * `combineOutcomes` in `programEvaluate.ts`.
 *
 * ## Three documents, three writers
 *
 * ```
 * programs/{program_id}          the policy   — super admin, rarely
 * program_records/{program_id}   flags, holds, overrides — capability-written
 * program_status/{program_id}    the computed answer — FUNCTION ONLY
 * ```
 *
 * Splitting the status out is what lets the rules say `allow write: if false`
 * on it. A client that could write its own GO would make every audit and every
 * email meaningless.
 *
 * ## Dates are `YYYY-MM-DD` strings, never timestamps
 *
 * A hold runs to the end of a school day and a criterion takes effect on a
 * morning. Millisecond timestamps invite off-by-one at midnight and around DST,
 * which is precisely the error dates exist here to remove. ISO date strings
 * sort lexically, so every comparison in this file is a string comparison.
 *
 * This module has no imports and should keep none: it is the vocabulary, and
 * staying a leaf is what lets `functions/src/scripts/*`, which run under
 * `ts-node --transpile-only`, import it without leaning on ts-node's
 * `experimentalResolver` to follow the `.js` specifiers the rest of `shared/`
 * uses. See the same note in `terms.ts`.
 */

/**
 * The four kinds of person a program can name.
 *
 * Declared here rather than in `programAccess.ts`, which is where the rest of
 * the access vocabulary lives, only because this module deliberately imports
 * nothing (see the note above) and a criterion needs to name one. The labels,
 * the defaults and every function that reasons about these are over there.
 */
export type ProgramRole = "administrator" | "office" | "advisor" | "teacher";

/** Comparisons a measure criterion may make. Deliberately four, not a language. */
export type CriterionOp = "<=" | "<" | ">=" | ">";

/**
 * A criterion's answer for one student.
 *
 * `unknown` is a third value and not a synonym for `pass`. A measure the app
 * cannot compute — no grades imported yet, a term code that does not resolve,
 * school days not loaded — must never read as "meets the standard". The
 * failure this project keeps getting bitten by is a window silently falling
 * back; here it surfaces instead, and a program with any unknown criterion
 * cannot come out GO.
 */
export type CriterionResult = "pass" | "fail" | "unknown";

/**
 * A program's overall answer.
 *
 * `unknown` exists for the same reason as above. The office needs a yes/no at
 * the desk, and it will get one — `unknown` is not a yes — but it must be told
 * the difference between "this student does not qualify" and "we cannot tell
 * you yet", because the first is a conversation with the student and the
 * second is a conversation with whoever runs the import.
 */
export type ProgramStatusValue = "go" | "no_go" | "unknown";

/** Shared by every criterion kind. */
interface CriterionBase {
  /** Stable within a program. Referenced by overrides and by stored status. */
  id: string;
  /**
   * The sentence the office reads, the email says and the student is told,
   * authored by whoever wrote the policy.
   *
   * Not derived from `measure + op + value`: that produces "courses below 2.7
   * <= 0", which is not something anybody should be handed.
   */
  label: string;
  /**
   * A few words for a table column header, when `label` is a whole sentence.
   *
   * "No more than 1 unexcused absence in the last two weeks" is the right thing
   * to tell a student and a terrible column header — it forces the column wide
   * enough to push everything else off screen. So a criterion carries both: the
   * sentence it is explained with, and the two words it is headed with. The
   * full label stays one hover away.
   */
  short_label?: string;
  /**
   * Longer prose for a "why not?" panel, when the label alone is too terse to
   * act on. Optional; most criteria do not need one.
   */
  detail?: string;
  /**
   * The criterion does not apply before this date (`YYYY-MM-DD`).
   *
   * Required by the real policy: "starting semester 2, all semester 1 classes
   * passed" is a criterion that must be inert until February, not one every
   * student fails all autumn.
   */
  effective_from?: string;
  /** The criterion stops applying after this date (`YYYY-MM-DD`). */
  effective_until?: string;
  /**
   * Which of the four program roles may enter this one. Any of them is enough.
   *
   * On the base rather than on the flag, because **a hold is a criterion like
   * any other**. There is no reason "no discipline hold" and "advisor has not
   * flagged a concern" should have different permission machinery: both are a
   * human deciding something about one student, and which humans may decide it
   * is a property of the criterion the policy author wrote.
   *
   * Absent falls back to a default that depends on the kind — the office for a
   * checkbox, an administrator for a hold. Starting points, not rules; see
   * `criterionRoles()`.
   *
   * On a measure criterion nobody types the number, so the roles name who
   * may grant an **exception** to it — "passing every core class, except this
   * student, until the 14th". Absent means administrators only.
   */
  write_roles?: ProgramRole[];
}

/**
 * A number the app computes, compared against a threshold.
 *
 * `measure` is a measure id, which may name a parameterized family — see
 * `programMeasures.ts`. The interesting variety lives there rather than in the
 * operators, which is why four operators are enough.
 */
export interface MeasureCriterion extends CriterionBase {
  kind: "measure";
  measure: string;
  op: CriterionOp;
  value: number;
  /**
   * Whether anybody may excuse this rule for one student until a date.
   *
   * Off unless the policy says so. Athletics warns before it benches, and the
   * warning is an exception; senior privileges has no "the principal says your
   * grades do not matter this month", and a button offering one would be a
   * policy the school never wrote. Enforced by the evaluator, not only by the
   * screen: an exception recorded against a rule that does not allow them
   * counts for nothing.
   */
  allow_exceptions?: boolean;
}

/** Whether a criterion takes exceptions at all. See `allow_exceptions`. */
export function criterionAllowsExceptions(criterion: Criterion): boolean {
  return criterion.kind === "measure" && criterion.allow_exceptions === true;
}

/**
 * A box a human ticks: a form on file, an advisor's endorsement.
 *
 * The criterion owns its box. An earlier version kept a separate `flags` list
 * on the program and had criteria point into it by id, which meant two things
 * to edit for one idea and a whole class of criterion-points-at-a-deleted-box
 * bugs the editor had to warn about.
 */
export interface FlagCriterion extends CriterionBase {
  kind: "flag";
  /**
   * The key this box is stored under in `program_records`.
   *
   * Separate from `id` and set once: records are keyed by it, so reusing the
   * criterion id directly would lose every tick the day somebody renamed a
   * criterion. Never shown to the author.
   */
  flag: string;
  /** Shown beside the control, for a box whose meaning is not self-evident. */
  help?: string;
}

/** Passes when no hold on this criterion covers today. */
export interface NoHoldCriterion extends CriterionBase {
  kind: "no_hold";
}

/**
 * Makes the student eligible while a waiver on this criterion covers today,
 * whatever the other criteria say — except a hold. See the module comment.
 *
 * With no waiver in force it is neutral: it neither passes nor fails anybody,
 * and it contributes no `CriterionOutcome`. That is what keeps an empty waiver
 * column from reading as a red cell, a "why not" line or an email reason.
 */
export interface WaiverCriterion extends CriterionBase {
  kind: "waiver";
}

export type Criterion =
  | MeasureCriterion
  | FlagCriterion
  | NoHoldCriterion
  | WaiverCriterion;

/**
 * Icons a program may wear, as icon names the webapp knows.
 *
 * A closed list rather than a free string: the icon set is a visual language
 * and every name in it means one thing (see `AGENTS.md`), so a policy author
 * picks from what exists rather than naming a file that may not. Adding one
 * here means drawing one.
 */
export const PROGRAM_ICONS = [
  "program-checklist",
  "senior-privileges",
  "athletic-eligibility",
] as const;

export type ProgramIcon = (typeof PROGRAM_ICONS)[number];

/** The generic one: a checked list, for a program that is neither of the others. */
export const DEFAULT_PROGRAM_ICON: ProgramIcon = "program-checklist";

/**
 * A population somebody keeps by hand, because the SIS does not know it.
 *
 * Teams are the case: Aspen does not export them and never will, so "the
 * athletes" is a list the athletics office maintains. Present means only the
 * students on the program's roster are evaluated; the school, grade and cohort
 * filters still apply on top, so a roster cannot reach past the program's
 * school.
 */
export interface ProgramRosterPolicy {
  /**
   * What a group on the roster is called — "Team". Absent means the roster
   * has no groups worth naming, and the screen offers none.
   */
  group_label?: string;
}

/** Which students the program evaluates. Reuses the analysis filter shape. */
export interface ProgramPopulation {
  grade_level?: number[];
  school_id?: string;
  /** Cohort (year of graduation) labels, when grade level is not the right cut. */
  cohort?: string[];
  /** Only students added to the program's roster. See `ProgramRosterPolicy`. */
  roster?: ProgramRosterPolicy;
}

/** One edit to a live policy, kept so a status change can be attributed. */
export interface ProgramEdit {
  at: number;
  by: string;
  summary: string;
}

/**
 * The stable half of a program's identity, across school years.
 *
 * `id` is `{slug}_{school_year}` — a new year is a new document, as honors does
 * it, so the id necessarily moves annually. The slug is what does not: it is
 * what a route reads, what a person recognises, and what ties this year's
 * senior privileges to last year's.
 *
 * Lower-case, digits and hyphens. Derived from the name at creation and
 * editable until first save, after which changing it would orphan every link
 * and every stored status keyed to the old id.
 */
export function programSlug(name: string): string {
  return (
    name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 48) || "program"
  );
}

/** `{slug}_{school_year}`, which is the document id. */
export function programId(slug: string, schoolYear: string): string {
  return `${slug}_${schoolYear}`;
}

/** The policy document: `programs/{program_id}`. */
export interface Program {
  id: string;
  /**
   * The part of `id` before the school year. Stable across years, and the
   * segment that appears in a URL.
   */
  slug: string;
  name: string;
  school_id: string;
  school_year: string;
  /** False keeps the definition without evaluating anybody against it. */
  enabled: boolean;
  /** Defaults to `DEFAULT_PROGRAM_ICON` when unset. */
  icon?: ProgramIcon;
  population: ProgramPopulation;
  criteria: Criterion[];
  /**
   * Editing a live policy is legitimate — that is the point of storing it — so
   * keep the trail. When a student's status flips, the first question is always
   * whether the student changed or the rule did.
   */
  edits?: ProgramEdit[];
  /**
   * Who may see this program and what they may do on it.
   *
   * Declared here rather than on each person's grant, so that adding a plan is
   * one edit in the policy editor instead of an edit to everybody's grant — and
   * so that a plan an audience should not know exists can simply omit them.
   *
   * Absent means `DEFAULT_PROGRAM_ACCESS`, which is exactly what the app did
   * before this field existed. See `programAccess.ts`, which owns the vocabulary
   * (this module stays import-free — see the note at the top).
   */
  access?: {
    audience: string;
    level: "view" | "contribute" | "manage";
    scope: "all" | "own";
  }[];
  /**
   * The offices whose staff may write to this program, or absent for every
   * office.
   *
   * **Derived from `access`, never edited**: `programOffices()` computes it and
   * `saveProgram` writes it. It exists because `firestore.rules` cannot loop
   * over a list of maps to find `office:` audiences, and can check a list of
   * strings. Empty means the program names no office at all.
   */
  offices?: string[];
  /**
   * Per-role read/write gates `firestore.rules` can check with one `get()`,
   * derived from `access` the same way `offices` is -- rules cannot loop over
   * `access`'s list of maps to find the `role:advisor` / `role:teacher` rows
   * and their levels, so `programRoleAccess()` in `programAccess.ts` flattens
   * them here and `saveProgram` writes them, never a human.
   *
   * Absent (a program saved before this field existed) defaults to what
   * `DEFAULT_PROGRAM_ACCESS` has always meant: advisors contribute, teachers
   * view. See `programRoleAccess()` for the exact defaults each rule falls
   * back to.
   */
  advisor_read?: boolean;
  advisor_write?: boolean;
  teacher_read?: boolean;
  teacher_write?: boolean;
  /** Who to talk to about a status. Named in anything sent to a student. */
  contact?: { name: string; email?: string };
  /**
   * What the overall answer is headed with — "Eligible", "Privileges".
   *
   * The yes/no column is the one every screen shows, and a word that is right
   * for senior privileges reads oddly above a list of athletes.
   */
  status_label?: string;
}

/** A human's answer to one flag. */
export interface FlagValue {
  value: boolean;
  at: number;
  by: string;
}

/**
 * A suspension of the privilege, appended rather than replaced.
 *
 * "There was an incident and I told them a month" happens more than once a
 * year, and the previous hold is exactly the context the next decision needs —
 * so this is an append-only list, not a mutable `suspended_until`.
 */
export interface ProgramHold {
  /**
   * Which `no_hold` criterion this hold answers.
   *
   * Required, so a policy can have more than one kind of hold. "No discipline
   * hold", placed by an administrator, and "no advisor hold", placed by the
   * advisor of record, are two criteria with two sets of `write_roles` — and
   * without naming the criterion, one hold would fail both.
   */
  criterion_id: string;
  /** `YYYY-MM-DD`. Omitted means "from whenever it was entered". */
  from?: string;
  /** `YYYY-MM-DD`, inclusive — the hold runs through the end of this day. */
  until: string;
  reason: string;
  at: number;
  by: string;
  /**
   * `YYYY-MM-DD` on which somebody ended this hold early.
   *
   * Explicit, rather than rewriting `until` to today: `until` is **inclusive**,
   * so "lift it" implemented as `until = today` leaves the hold in force for
   * the rest of the day — the write succeeds, the screen does not change, and
   * it looks like nothing happened. That was a real bug.
   *
   * Keeping both dates also keeps the history honest. "Meant to run to 14
   * November, lifted on 20 September" is the thing somebody asks about later;
   * a rewritten `until` erases the question.
   */
  lifted_at?: string;
  lifted_by?: string;
}

/**
 * "Eligible regardless, until this date": a hold in reverse.
 *
 * The same shape as `ProgramHold`, for the same reasons — append-only so the
 * last waiver is context for the next decision, `until` inclusive, and
 * `lifted_at` rather than a rewritten `until` to end one early.
 *
 * Not a `ProgramOverride`. An override excuses **one rule** and never rescues
 * an `unknown`; a waiver answers the whole program and does, because "eligible
 * regardless" is what the person granting it said.
 */
export interface ProgramWaiver {
  /** Which `waiver` criterion this answers. Required: a policy may have two. */
  criterion_id: string;
  /** `YYYY-MM-DD`. Omitted means "from whenever it was entered". */
  from?: string;
  /** `YYYY-MM-DD`, inclusive — the waiver runs through the end of this day. */
  until: string;
  reason: string;
  at: number;
  by: string;
  /** `YYYY-MM-DD` on which somebody ended it early. See `ProgramHold.lifted_at`. */
  lifted_at?: string;
  lifted_by?: string;
}

/**
 * An administrator excusing one criterion for one student, until a date.
 *
 * This is the alternative to putting `OR` in the criteria language. A stored
 * override carries the reason, the author and the expiry — all of which you
 * want the first time a parent calls, and all of which a disjunct throws away.
 */
export interface ProgramOverride {
  criterion_id: string;
  /** `YYYY-MM-DD`, inclusive. */
  until: string;
  reason: string;
  at: number;
  by: string;
  /**
   * `YYYY-MM-DD` on which somebody ended it early — the student's grade came
   * up, or the exception was a mistake. Explicit rather than a rewritten
   * `until`, for the reason `ProgramHold.lifted_at` gives: `until` is
   * inclusive, and "meant to run to the 14th, ended on the 9th" is the history
   * somebody asks about later.
   */
  lifted_at?: string;
  lifted_by?: string;
}

/**
 * A student's place on a hand-kept roster.
 *
 * `student_name` is written when the student is added, for the same reason the
 * evaluator denormalizes names into the status document: the roster screen
 * prints every athlete before anybody has run a check, and fetching a student
 * document per athlete to do it is the per-entity read `AGENTS.md` says to stop
 * at. The name is only a label — the evaluator reads the real one each run.
 */
export interface ProgramRosterEntry {
  student_name: string;
  /**
   * Denormalized the same way and for the same reason as `student_name`
   * above, from `RosterCandidate.email` (itself from `lookups/student_summaries`
   * or, lacking that rollup, a live student read) at add time. Absent for a
   * student with no address on file, or added before this field existed.
   */
  student_email?: string;
  /** Teams, for a roster whose policy names its groups. A student may play two. */
  groups?: string[];
  at: number;
  by: string;
}

/** What humans entered about one student. */
export interface StudentProgramRecord {
  flags?: Record<string, FlagValue>;
  holds?: ProgramHold[];
  overrides?: ProgramOverride[];
  waivers?: ProgramWaiver[];
  /**
   * Present while the student is on the roster.
   *
   * Removing a student deletes this and nothing else, so the holds and
   * overrides survive a student leaving the team mid-season and are there if
   * they come back.
   */
  roster?: ProgramRosterEntry;
}

/** `program_records/{program_id}` — one document per program, all students. */
export interface ProgramRecord {
  program_id: string;
  students: Record<string, StudentProgramRecord>;
}

/**
 * One thing that counted toward a criterion's number.
 *
 * The number alone ends the conversation in the wrong place. "Two courses below
 * a B-" is the office's answer and "*which* two" is the student's next
 * question — and for attendance the date is not curiosity, it is actionable: a
 * student arriving with a note for the 18th can have that tardy excused, and
 * the office can only find it if the row says which day it was.
 *
 * Denormalized into the status document for the same reason the labels are:
 * the office may not be able to read the underlying records, and nobody should
 * have to re-derive a verdict to explain it.
 */
export interface CriterionEvidence {
  /** A course name, or what kind of event this was. */
  label: string;
  /** `YYYY-MM-DD`, for dated events. */
  date?: string;
  /** The number behind it — a grade, usually. */
  value?: number;
  /**
   * The section a grade came from, so a reader can join `lookups/sections`
   * for who teaches it. The label alone cannot: "Algebra I" is several
   * sections with different teachers. Absent on dated events, and on grade
   * evidence written before this field existed.
   */
  section_id?: string;
}

/**
 * How many evidence items one criterion may carry into the status document.
 *
 * `program_status` is a single document for every student in the program and
 * Firestore caps a document at 1 MiB. Ten items of under a hundred bytes, on the
 * criteria a student actually fails, is a few KB across a senior class and
 * still bounded if a program ever covers the whole school. A student with
 * eleven failing courses does not need the eleventh listed to get the point.
 */
export const MAX_EVIDENCE = 10;

/** A criterion the student did not pass, denormalized for display. */
export interface CriterionOutcome {
  criterion_id: string;
  /** Copied from the policy at evaluation time, so a later edit cannot rewrite history. */
  label: string;
  result: CriterionResult;
  /** "2 courses below 2.7" — the number behind the verdict, as a phrase. */
  detail?: string;
  /**
   * The bare number, for a table cell.
   *
   * The phrase is what a person is *told*; the number is what a column shows.
   * A cell wide enough for "2 unexcused absences in the last 14 days" is a
   * column that pushes the next three off the screen, and the header already
   * says what is being counted.
   */
  value?: number;
  /**
   * What made up that number, capped at `MAX_EVIDENCE`.
   *
   * Carried only for criteria the student did not pass: a list of every course
   * somebody is passing is bulk with no reader.
   */
  evidence?: CriterionEvidence[];
  /** True when `evidence` was cut short by the cap. */
  evidence_truncated?: boolean;
  /** Set when an override carried this criterion; the date it lapses. */
  overridden_until?: string;
  /**
   * Why, when an override carried it. Its own field rather than written over
   * `detail`, so "1 class below 1.8" is still there to read beside the reason
   * — and still there when the exception lapses.
   */
  override_reason?: string;
  /** Set on a `no_hold` failure: the date the hold lifts. */
  until?: string;
}

/**
 * One student's computed answer.
 *
 * `student_name` and `advisor` are denormalized from the student document by
 * the evaluator, which has it in hand anyway. This is what lets the main
 * office's desk lookup cost **two reads** — the policy and this document —
 * rather than one per student in the program. A page that had to fetch 85
 * student documents to print 85 names is exactly the shape `AGENTS.md` says to
 * stop and reconsider, and the write path already knows the answer.
 */
export interface StudentProgramStatus {
  /** Denormalized so a lookup screen needs no student reads. */
  student_name?: string;
  /**
   * Denormalized for the same reason, and because any message about a
   * privilege has to name a human the student can actually talk to.
   */
  advisor?: string;
  status: ProgramStatusValue;
  /** Every criterion's outcome, in policy order. */
  criteria: CriterionOutcome[];
  /** The subset that did not pass — what the office reads out. */
  failing: CriterionOutcome[];
  /** Denormalized from a blocking hold, when one is the reason. */
  until?: string;
  /**
   * The waiver in force, when there is one — present even when a hold beat it,
   * so a screen can say "waived, but held". `status` is the answer; this is
   * only why it is that answer.
   *
   * Waiver criteria produce no entry in `criteria`, so this is the only place
   * a waiver shows up in the verdict.
   */
  waiver?: ActiveWaiver;
  /** What the student was last told, so re-running cannot double-send. */
  notified_status?: ProgramStatusValue;
  notified_at?: number;
}

/** A waiver as the verdict carries it: which criterion, until when, and why. */
export interface ActiveWaiver {
  criterion_id: string;
  /** Copied from the policy at evaluation time, as outcome labels are. */
  label: string;
  until: string;
  reason: string;
}

/** `program_status/{program_id}` — function-written, client-unwritable. */
export interface ProgramStatusDoc {
  program_id: string;
  computed_at: number;
  /** `edits.length` when this ran, so a status can be tied to a policy version. */
  policy_version: number;
  students: Record<string, StudentProgramStatus>;
}

/** Firestore paths for program documents. */
export interface ProgramPaths {
  "programs/{program_id}": Program;
  "program_records/{program_id}": ProgramRecord;
  "program_status/{program_id}": ProgramStatusDoc;
}

/**
 * Whether a criterion is in force on `today`.
 *
 * Both bounds inclusive: a criterion effective from the first day of semester 2
 * applies on that day.
 */
export function criterionApplies(criterion: Criterion, today: string): boolean {
  if (criterion.effective_from && today < criterion.effective_from)
    return false;
  if (criterion.effective_until && today > criterion.effective_until)
    return false;
  return true;
}

/** Student ids on a program's roster, in no particular order. */
export function rosterStudentIds(record: ProgramRecord | null | undefined): string[] {
  return Object.entries(record?.students ?? {})
    .filter(([, entry]) => entry?.roster)
    .map(([studentId]) => studentId);
}

/** Every group named on a roster, sorted, for a filter or a suggestion list. */
export function rosterGroups(record: ProgramRecord | null | undefined): string[] {
  const groups = new Set<string>();
  for (const entry of Object.values(record?.students ?? {}))
    for (const group of entry?.roster?.groups ?? []) groups.add(group);
  return [...groups].sort((a, b) => a.localeCompare(b));
}

/** Holds covering `today`, most recently ending last. */
export function activeHolds(
  record: StudentProgramRecord | undefined,
  today: string,
  /**
   * Narrow to the holds one `no_hold` criterion answers. Omitted returns every
   * hold in force, which is what a screen listing the whole ledger wants.
   */
  criterionId?: string,
): ProgramHold[] {
  return (record?.holds ?? [])
    .filter((hold) => !hold.lifted_at || today < hold.lifted_at)
    .filter((hold) => (!hold.from || hold.from <= today) && today <= hold.until)
    // A hold that names no criterion predates the field and answers all of
    // them, which is what it has always meant.
    .filter(
      (hold) =>
        !criterionId || !hold.criterion_id || hold.criterion_id === criterionId,
    )
    .sort((a, b) => a.until.localeCompare(b.until));
}

/**
 * Waivers covering `today`, most recently ending last — `activeHolds`' twin.
 *
 * Unlike a hold, a waiver must name its criterion: there are no waivers from
 * before the field existed, so a missing one is a malformed record rather
 * than one that answers every criterion.
 */
export function activeWaivers(
  record: StudentProgramRecord | undefined,
  today: string,
  criterionId?: string,
): ProgramWaiver[] {
  return (record?.waivers ?? [])
    .filter((waiver) => !waiver.lifted_at || today < waiver.lifted_at)
    .filter(
      (waiver) => (!waiver.from || waiver.from <= today) && today <= waiver.until,
    )
    .filter((waiver) => !criterionId || waiver.criterion_id === criterionId)
    .sort((a, b) => a.until.localeCompare(b.until));
}

/**
 * The waiver making this student eligible today, if any: the longest-running
 * active waiver on any `waiver` criterion in force today.
 *
 * Any one suffices. A waiver recorded against a criterion the policy no longer
 * has — or one outside its effective window — counts for nothing, as an
 * exception against a rule that does not allow them counts for nothing.
 */
export function programWaiver(
  criteria: readonly Criterion[],
  record: StudentProgramRecord | undefined,
  today: string,
): ActiveWaiver | undefined {
  let best: ActiveWaiver | undefined;
  for (const criterion of criteria) {
    if (criterion.kind !== "waiver" || !criterionApplies(criterion, today))
      continue;
    const waiver = activeWaivers(record, today, criterion.id).at(-1);
    if (waiver && (!best || waiver.until > best.until))
      best = {
        criterion_id: criterion.id,
        label: criterion.label,
        until: waiver.until,
        reason: waiver.reason,
      };
  }
  return best;
}

/** The override in force for a criterion on `today`, if any. */
export function activeOverride(
  record: StudentProgramRecord | undefined,
  criterionId: string,
  today: string,
): ProgramOverride | undefined {
  return (record?.overrides ?? [])
    .filter((o) => o.criterion_id === criterionId && today <= o.until)
    .filter((o) => !o.lifted_at || today < o.lifted_at)
    .sort((a, b) => b.until.localeCompare(a.until))[0];
}

/**
 * Whether a student is in a program's population.
 *
 * **An inactive student is in no population, whatever else matches.** Grade
 * level does not move when a student leaves: last year's seniors are still
 * `grade_level: 12` and are only distinguishable by `is_active`, so a filter
 * that checks the grade alone quietly fills the office's list with students who
 * graduated. Enforced here rather than at each call site, because every caller
 * would otherwise have to remember it and one of them already did not.
 */
export function inPopulation(
  population: ProgramPopulation,
  // Deliberately **required**, not optional. When these were optional a caller
  // passed a summary with no `school_id`, every student compared unequal to
  // "HS", and the screen matched nobody — a silent, total, compile-clean
  // failure. Required fields turn that into a type error at the call site.
  student: {
    grade_level: number;
    school_id: string;
    is_active: boolean | undefined;
    cohort?: string;
  },
  /**
   * What humans entered for this student, for a roster-kept population.
   *
   * Required for the same reason the student fields are: an optional argument
   * here is one a caller forgets, and a forgotten roster check evaluates every
   * student in the school against a policy meant for forty of them. Pass
   * `undefined` explicitly for a student with no record.
   */
  record: StudentProgramRecord | undefined,
): boolean {
  // Only an explicit `false` excludes. A missing `is_active` means nobody has
  // said either way, and treating that as "gone" would empty the list on a
  // single unmigrated field — which is a far more likely accident than a
  // graduated student sneaking in. Roster reconciliation writes `is_active:
  // false` alongside `inactive_at` when a student actually leaves, so the
  // signal that matters is always present when it matters.
  if (student.is_active === false) return false;
  const { grade_level, school_id, cohort } = population;
  if (population.roster && !record?.roster) return false;
  if (school_id && student.school_id !== school_id) return false;
  if (
    grade_level?.length &&
    !grade_level.includes(student.grade_level as number)
  )
    return false;
  if (cohort?.length && !(student.cohort && cohort.includes(student.cohort)))
    return false;
  return true;
}

/**
 * A one-line description of what changed between two versions of a policy.
 *
 * The edit trail exists so that when a student's status flips, somebody can
 * tell whether the student changed or the rule did. That question is worth
 * answering — but it was being answered by *requiring* an administrator to type
 * a commit message before the save button would light up, which is a developer's
 * habit imposed on an office. Nobody editing a tardy allowance is writing
 * release notes, and a mandatory field mostly collects "update" and "fix".
 *
 * So the note is optional now and this fills the gap. What it produces is
 * coarser than a person's sentence and more reliable than what a person types
 * under protest: it reads the two documents and says which parts moved. A typed
 * summary always wins when there is one, because only a human can say *why*.
 */
export function describeProgramChange(before: Program, after: Program): string {
  const parts: string[] = [];

  if (before.name !== after.name) parts.push(`renamed to "${after.name}"`);
  if (before.enabled !== after.enabled)
    parts.push(after.enabled ? "turned on" : "turned off");

  const beforeCriteria = new Map(before.criteria.map((c) => [c.id, c]));
  const afterCriteria = new Map(after.criteria.map((c) => [c.id, c]));
  const added = after.criteria.filter((c) => !beforeCriteria.has(c.id)).length;
  const removed = before.criteria.filter((c) => !afterCriteria.has(c.id)).length;
  // Compared as JSON because a criterion is a flat data object: every field
  // that distinguishes two versions is serializable, and listing them by hand
  // is a list that goes stale the next time the vocabulary grows.
  const edited = after.criteria.filter((c) => {
    const previous = beforeCriteria.get(c.id);
    return previous && JSON.stringify(previous) !== JSON.stringify(c);
  }).length;

  if (added) parts.push(`added ${added} ${added === 1 ? "rule" : "rules"}`);
  if (removed)
    parts.push(`removed ${removed} ${removed === 1 ? "rule" : "rules"}`);
  if (edited)
    parts.push(`changed ${edited} ${edited === 1 ? "rule" : "rules"}`);

  if (JSON.stringify(before.population) !== JSON.stringify(after.population))
    parts.push("changed who it covers");
  if (JSON.stringify(before.contact ?? null) !== JSON.stringify(after.contact ?? null))
    parts.push("changed the contact");
  if ((before.status_label ?? "") !== (after.status_label ?? ""))
    parts.push("renamed the status column");
  if (before.icon !== after.icon) parts.push("changed the icon");

  // "Saved with no changes" rather than an empty string: a trail entry that
  // says nothing is worse than one that says nothing happened, because the
  // reader cannot tell which of the two it is looking at.
  if (!parts.length) return "Saved with no changes";
  return parts.join(", ").replace(/^./, (c) => c.toUpperCase());
}
